
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Release, Status, Environment, SubItem, SubItemStatus, Source } from '@/types';
import { useToast } from '@/components/ui/use-toast';
import { v4 as uuidv4 } from 'uuid';

export const useReleases = (hookClientId?: string) => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Use useCallback to memoize the fetchReleases function so it doesn't cause infinite loops
  const fetchReleases = useCallback(async () => {
    console.log('[useReleases.fetchReleases] Fetching releases for client (hookClientId):', hookClientId);
    setIsLoading(true);
    try {
      let query = supabase
        .from("releases")
        .select('*')
        .order('created_at', { ascending: false });
      
      // Filter by client - this is now required
      if (hookClientId) {
        query = query.eq('client_id', hookClientId);
      } else {
        // If no client ID provided, don't fetch any releases
        setReleases([]);
        setIsLoading(false);
        return;
      }

      const { data: releasesData, error: releasesError } = await query;

      if (releasesError) {
        console.error('Error fetching releases:', releasesError);
        toast({
          title: 'Error fetching releases',
          description: releasesError.message,
          variant: 'destructive',
        });
        return;
      }

      console.log('[useReleases] Fetched releases count:', releasesData?.length || 0);

      // Fetch all sub-items for this client's releases
      const releaseIds = releasesData.map((release: any) => release.id);
      
      let subItemsData: any[] = [];
      if (releaseIds.length > 0) {
        const { data: fetchedSubItems, error: subItemsError } = await supabase
          .from("sub_items")
          .select('*')
          .in('release_id', releaseIds);
        
        if (subItemsError) {
          console.error('Error fetching sub-items:', subItemsError);
          toast({
            title: 'Error fetching sub-items',
            description: subItemsError.message,
            variant: 'destructive',
          });
        } else {
          subItemsData = fetchedSubItems || [];
          console.log('[useReleases] Fetched sub-items count:', subItemsData.length);
        }
      }

      // Transform the data to match our Release type
      const parsedReleases = releasesData.map((release: any) => {
        // Find all sub-items for this release
        const releaseSubItems = subItemsData
          .filter((subItem: any) => subItem.release_id === release.id)
          .map((subItem: any) => ({
            id: subItem.id,
            name: subItem.name,
            status: subItem.status as SubItemStatus,
            source: subItem.source as Source,
            approvedBy: subItem.approved_by,
            type: subItem.type,
            sourceLink: subItem.source_link,
            pagesAffected: subItem.pages_affected || [],
            collectionsAffected: subItem.collections_affected || [],
            componentsAffected: subItem.components_affected || [],
            notes: subItem.notes,
            releaseId: subItem.release_id
          }));

        return {
          id: release.id,
          version: release.version,
          status: release.status as Status,
          environment: release.environment as Environment,
          brdLink: release.brd_link,
          approvedBy: release.approved_by,
          notes: release.notes,
          createdAt: new Date(release.created_at),
          plannedReleaseDate: release.planned_release_date ? new Date(release.planned_release_date) : undefined,
          subItems: releaseSubItems,
          clientId: release.client_id,
          ownerId: release.owner_id,
        };
      });

      console.log('[useReleases] Setting releases with count:', parsedReleases.length);
      setReleases(parsedReleases);
    } catch (error) {
      console.error('Unexpected error during fetchReleases:', error);
      toast({
        title: 'Unexpected error',
        description: 'Failed to fetch releases',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [hookClientId, toast]);

  // Load releases from Supabase on component mount or clientId change
  useEffect(() => {
    if (hookClientId) {
      fetchReleases();
    }
  }, [hookClientId, fetchReleases]);

  const addRelease = async (newRelease: Omit<Release, 'id'>) => {
    console.log('[useReleases.addRelease] Received newRelease payload:', JSON.stringify(newRelease, null, 2));
    console.log('[useReleases.addRelease] hookClientId (context for this hook instance):', hookClientId);

    // This guard checks if the useReleases hook itself has a valid client context to operate within.
    // The actual client_id for the release record will come from newRelease.clientId.
    if (!hookClientId) {
      console.error('[useReleases.addRelease] hookClientId is undefined. The hook is not initialized with a client context.');
      toast({
        title: 'Internal Hook Error',
        description: 'useReleases hook is not initialized with a client ID.',
        variant: 'destructive',
      });
      throw new Error('hookClientId is undefined in useReleases.addRelease');
    }

    try {
      // Find the latest version number
      const sortedReleases = [...releases].sort((a, b) => {
        const versionA = parseInt(a.version, 10);
        const versionB = parseInt(b.version, 10);
        return versionB - versionA;
      });
      
      // Determine new version number (next sequential number)
      let newVersion: string;
      
      if (sortedReleases.length === 0) {
        // First release ever - start with 1
        newVersion = '1';
      } else {
        // Get the latest version and increment by 1
        const latestVersion = parseInt(sortedReleases[0].version, 10);
        newVersion = (latestVersion + 1).toString();
      }

      // Generate a UUID for the new release
      const newId = uuidv4();
      const releaseWithVersion = { ...newRelease, version: newVersion };
      
      console.log('[useReleases] Creating release with version:', newVersion);
      
      // Validate the clientId to ensure it's not empty
      if (!releaseWithVersion.clientId) {
        console.error('[useReleases.addRelease] clientId is empty or undefined');
        toast({
          title: 'Validation Error',
          description: 'Client ID is required to create a release',
          variant: 'destructive',
        });
        throw new Error('clientId is required in useReleases.addRelease');
      }

      // Log the data we're about to insert
      console.log('[useReleases.addRelease] Inserting release with data:', {
        id: newId,
        version: releaseWithVersion.version,
        client_id: releaseWithVersion.clientId,
        owner_id: releaseWithVersion.ownerId || null
      });
      
      // First, insert the release without sub-items
      const { data, error } = await supabase
        .from("releases")
        .insert({
          id: newId,
          version: releaseWithVersion.version,
          status: releaseWithVersion.status,
          environment: releaseWithVersion.environment,
          brd_link: releaseWithVersion.brdLink || '',
          approved_by: releaseWithVersion.approvedBy || '',
          notes: releaseWithVersion.notes || '',
          created_at: releaseWithVersion.createdAt.toISOString(),
          planned_release_date: releaseWithVersion.plannedReleaseDate?.toISOString(),
          client_id: releaseWithVersion.clientId, // Validated above
          owner_id: releaseWithVersion.ownerId || null, // Make null if undefined
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error adding release:', error);
        toast({
          title: 'Error adding release',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }

      // If there are sub-items, insert them separately
      if (releaseWithVersion.subItems && releaseWithVersion.subItems.length > 0) {
        const subItemsForDb = releaseWithVersion.subItems.map(item => ({
          id: uuidv4(), // Generate UUID for each sub-item
          release_id: newId,
          name: item.name,
          status: item.status,
          source: item.source,
          approved_by: item.approvedBy,
          type: item.type,
          source_link: item.sourceLink,
          pages_affected: item.pagesAffected,
          collections_affected: item.collectionsAffected,
          components_affected: item.componentsAffected,
          notes: item.notes
        }));

        const { error: subItemsError } = await supabase
          .from("sub_items")
          .insert(subItemsForDb);

        if (subItemsError) {
          console.error('Error adding sub items:', subItemsError);
          toast({
            title: 'Error adding sub items',
            description: subItemsError.message,
            variant: 'destructive',
          });
          // Continue despite sub-item errors as the release is already created
        }
      }

      toast({
        title: 'Success',
        description: `Release version ${newVersion} created`,
      });

      // Update local state with the new release
      const newReleaseWithId: Release = { 
        ...releaseWithVersion, 
        id: newId,
        subItems: releaseWithVersion.subItems ? releaseWithVersion.subItems.map(item => ({
          ...item,
          id: uuidv4(), // Temporary ID until we refresh
          releaseId: newId
        })) : []
      };
      
      // Immediately update the local state for a responsive UI
      setReleases(prevReleases => [...prevReleases, newReleaseWithId]);
      
      console.log('[useReleases] Refreshing releases after adding new release');
      // Refresh to get the actual subitem IDs and ensure consistency with the database
      await fetchReleases();
      
      // Return the new release ID
      return newId;
    } catch (error) {
      console.error('Unexpected error during addRelease:', error);
    // Toast for this error is typically handled by the calling component (ClientReleases.tsx)
    // which catches the thrown error. If not, this toast would be a fallback:
    // toast({
    //   title: 'Unexpected Error',
    //   description: 'Failed to add release due to an unexpected issue.',
    //   variant: 'destructive',
    // });
      throw error;
    }
  };

  const updateRelease = async (updatedRelease: Release) => {
    console.log('[useReleases] Updating release:', updatedRelease.id);
    try {
      // Update the release record
      const { error } = await supabase
        .from("releases")
        .update({
          status: updatedRelease.status,
          environment: updatedRelease.environment,
          brd_link: updatedRelease.brdLink,
          approved_by: updatedRelease.approvedBy,
          notes: updatedRelease.notes,
          planned_release_date: updatedRelease.plannedReleaseDate?.toISOString(),
        })
        .eq('id', updatedRelease.id);

      if (error) {
        console.error('Error updating release:', error);
        toast({
          title: 'Error updating release',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }

      // Handle sub item changes
      // 1. Get current sub items for this release
      const { data: currentSubItems, error: fetchError } = await supabase
        .from('sub_items')
        .select('id')
        .eq('release_id', updatedRelease.id);
      
      if (fetchError) {
        console.error('Error fetching current sub items:', fetchError);
        throw fetchError;
      }
      
      // Get current sub item IDs
      const currentIds = currentSubItems?.map(item => item.id) || [];
      // Get updated sub item IDs
      const updatedIds = updatedRelease.subItems.map(item => item.id);
      
      // Find items to delete (in current but not in updated)
      const idsToDelete = currentIds.filter(id => !updatedIds.includes(id));
      
      // Delete removed items
      if (idsToDelete.length > 0) {
        console.log('[useReleases] Deleting sub items:', idsToDelete);
        const { error: deleteError } = await supabase
          .from('sub_items')
          .delete()
          .in('id', idsToDelete);
          
        if (deleteError) {
          console.error('Error deleting sub items:', deleteError);
          // Continue despite errors
        }
      }
      
      console.log('[useReleases] Processing sub items for update/insert:', updatedRelease.subItems.length);
      
      // Update existing and add new items
      for (const item of updatedRelease.subItems) {
        const subItemData = {
          release_id: updatedRelease.id,
          name: item.name,
          status: item.status,
          source: item.source,
          approved_by: item.approvedBy,
          type: item.type,
          source_link: item.sourceLink,
          pages_affected: item.pagesAffected,
          collections_affected: item.collectionsAffected,
          components_affected: item.componentsAffected,
          notes: item.notes
        };
        
        if (currentIds.includes(item.id)) {
          // Update existing item
          console.log('[useReleases] Updating existing sub item:', item.id);
          const { error: updateError } = await supabase
            .from('sub_items')
            .update(subItemData)
            .eq('id', item.id);
            
          if (updateError) {
            console.error(`Error updating sub item ${item.id}:`, updateError);
            toast({
              title: 'Error updating sub item',
              description: updateError.message,
              variant: 'destructive',
            });
            // Continue despite errors
          }
        } else {
          // Insert new item
          console.log('[useReleases] Inserting new sub item');
          const { error: insertError } = await supabase
            .from('sub_items')
            .insert({
              id: item.id,  // Always include the ID we generated
              ...subItemData
            });
            
          if (insertError) {
            console.error('Error inserting sub item:', insertError);
            toast({
              title: 'Error adding sub item',
              description: insertError.message,
              variant: 'destructive',
            });
            // Continue despite errors
          }
        }
      }

      toast({
        title: 'Success',
        description: `Release version ${updatedRelease.version} updated`,
      });

      // Immediately update the local state for a responsive UI
      setReleases((prevReleases) => 
        prevReleases.map((release) => 
          release.id === updatedRelease.id ? updatedRelease : release
        )
      );
      
      console.log('[useReleases] Refreshing releases after update');
      // Refresh to ensure consistency with the database
      await fetchReleases();
      
      // Return the updated release ID
      return updatedRelease.id;
    } catch (error) {
      console.error('Unexpected error during updateRelease:', error);
      toast({
        title: 'Unexpected error',
        description: 'Failed to update release',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteRelease = async (id: string) => {
    console.log('[useReleases] Deleting release:', id);
    try {
      // Delete from Supabase - the cascade will delete related sub_items
      const { error } = await supabase
        .from("releases")
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting release:', error);
        toast({
          title: 'Error deleting release',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }

      toast({
        title: 'Success',
        description: 'Release deleted',
      });

      // Update local state immediately
      setReleases((prevReleases) => prevReleases.filter((release) => release.id !== id));
      
      // Return the deleted release ID
      return id;
    } catch (error) {
      console.error('Unexpected error during deleteRelease:', error);
      toast({
        title: 'Unexpected error',
        description: 'Failed to delete release',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Function to move a sub-item to a different release
  const moveSubItem = async (subItemId: string, targetReleaseId: string) => {
    console.log('[useReleases] Moving sub item:', subItemId, 'to release:', targetReleaseId);
    try {
      // Update the release_id of the sub-item
      const { error } = await supabase
        .from('sub_items')
        .update({ release_id: targetReleaseId })
        .eq('id', subItemId);

      if (error) {
        console.error('Error moving sub-item:', error);
        toast({
          title: 'Error moving sub-item',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }

      toast({
        title: 'Success',
        description: 'Sub-item moved to different release',
      });

      console.log('[useReleases] Refreshing releases after moving sub item');
      // Refresh releases to update the UI
      await fetchReleases();
      
      return subItemId;
    } catch (error) {
      console.error('Unexpected error during moveSubItem:', error);
      toast({
        title: 'Unexpected error',
        description: 'Failed to move sub-item',
        variant: 'destructive',
      });
      throw error;
    }
  };

  return {
    releases,
    isLoading,
    addRelease,
    updateRelease,
    deleteRelease,
    fetchReleases,
    moveSubItem
  };
};
